<template>
    <!-- Admin, ProProxyUI servers: what customers on this server can use, and the speed label they see.
         OS Spoofing and UDP Traffic are the server's Finger Print / Socks UDP switches (the Buy page filters on them). -->
    <div class="features-box">
        <p class="features-box__title">Features</p>
        <p class="features-box__hint">What this server offers customers. Off = removed from the package cards and from My Proxy.</p>
        <div class="features-box__list">
            <label v-for="f in FEATURES" :key="f.key" class="features-box__item" :class="{ 'is-off': !isOn(f.key) }">
                <input type="checkbox" class="features-box__input" :checked="isOn(f.key)" @change="toggle(f.key, $event.target.checked)" />
                <span class="features-box__switch" aria-hidden="true"></span>
                <span class="features-box__text">
                    <span class="features-box__name">{{ f.label }}</span>
                    <span class="features-box__state">{{ isOn(f.key) ? 'On' : 'Off' }}</span>
                </span>
            </label>
        </div>
        <label class="features-box__speed">
            <span class="features-box__name">Max speed shown to customers</span>
            <span class="features-box__speedrow">
                <input type="number" min="1" step="1" placeholder="No limit" class="form-control shadow-none rounded-0 text-black features-box__speedinput"
                    :value="maxSpeed ?? ''" @input="emit('update:maxSpeed', $event.target.value === '' ? null : Number($event.target.value))" />
                <span>Mbps</span>
            </span>
            <span class="features-box__state">Shown as "Up to 10 Mbps" on the package cards and the purchase window. A label only: set the real cap on the box itself. Leave empty for no limit.</span>
        </label>
    </div>
</template>

<script setup>
import { defineEmits, defineProps } from 'vue';

const FEATURES = [
    { key: 'openvpn', label: 'OpenVPN' },
    { key: 'wireguard', label: 'WireGuard' },
    { key: 'os_spoofing', label: 'OS Spoofing' },
    { key: 'udp', label: 'UDP Traffic' },
];
const props = defineProps({
    modelValue: { type: Object, default: () => ({}) },   // { openvpn, wireguard, os_spoofing, udp }; missing = on
    maxSpeed: { type: Number, default: null },           // Mbps, null = no label
});
const emit = defineEmits(['update:modelValue', 'update:maxSpeed']);
const isOn = (key) => props.modelValue?.[key] !== false;
const toggle = (key, value) => emit('update:modelValue', { ...props.modelValue, [key]: value });
</script>

<style scoped>
.features-box { margin: 4px 8px 12px; padding: 12px; border: 1px solid #dee2e6; border-radius: 10px; clear: both; }
.features-box__title { margin: 0 0 2px; font-weight: 600; }
.features-box__hint { margin: 0 0 10px; font-size: 12px; color: #6b7280; }
.features-box__list { display: flex; flex-wrap: wrap; gap: 8px; }
.features-box__item { position: relative; display: flex; align-items: center; gap: 10px; flex: 1; min-width: 150px; margin: 0; padding: 8px 10px; border: 1px solid #dee2e6; border-radius: 10px; background: #ffffff; cursor: pointer; }
.features-box__item.is-off { opacity: 0.6; }
.features-box__input { position: absolute; opacity: 0; width: 0; height: 0; }
.features-box__switch { position: relative; flex: none; width: 34px; height: 20px; border-radius: 999px; background: #adb5bd; transition: background-color 150ms ease; }
.features-box__switch::after { content: ""; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #ffffff; transition: left 150ms ease; }
.features-box__input:checked + .features-box__switch { background: #6366f1; }
.features-box__input:checked + .features-box__switch::after { left: 16px; }
.features-box__input:focus-visible + .features-box__switch { outline: 2px solid #6366f1; outline-offset: 2px; }
.features-box__speed { display: flex; flex-direction: column; gap: 4px; margin: 12px 0 0; }
.features-box__speedrow { display: flex; align-items: center; gap: 8px; }
.features-box__speedinput { width: 140px; }
.features-box__text { display: flex; flex-direction: column; line-height: 1.25; }
.features-box__name { font-weight: 600; font-size: 13.5px; }
.features-box__state { font-size: 11.5px; color: #6b7280; }
</style>

<style>
/* dark theme: the site's own panel colours (unscoped so body.dark reaches it) */
body.dark .features-box { border-color: #45445e; }
body.dark .features-box__item { border-color: #45445e; background: #34334a; }
body.dark .features-box__hint, body.dark .features-box__state { color: #bcbbc7; }
body.dark .features-box__switch { background: #45445e; }
</style>
